import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/inteligencia-black25")({
  head: () => ({
    meta: [
      { title: "Inteligência Black November 2025 · FEO" },
      { name: "description", content: "Estudo interno da Black November 2025: etapas, métricas, gastos, cupons e balizadores para metas 2026." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Inteligência Black November 2025 · FEO" },
      { property: "og:description", content: "Estudo interno da ação Black November 2025 da FEO." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const brl = (v: number, d = 0) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: d, maximumFractionDigits: d });
const num = (v: number, d = 0) => v.toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });

const ACAO = { fat: 1056293, invest: 60858.73, vendas: 1077, sessoes: 6040, ticket: 980.77, cpv: 56.51, roas: 17.36, conv: 17.83 };
const MES = { fat: 862117.31, gasto: 233071.29, saldo: 629046.02, roas: 3.7, pagas: 425, abertas: 141, meta: 900000 };

/** Realizado de novembro/2025 por linha de produto (Placar do Jogo + planilha mestre). */
const REALIZADO = {
  pos: { vendas: 425, fat: 858066.5 },
  posDobro: { vendas: 209 },
  posCatalogo: { vendas: 216 },
  outros: { vendas: 517, fat: 105684 },
  star: { vendas: 268, fat: 41788 },
  cert: { vendas: 249, fat: 49424 },
  extensoes: { fat: 15654 },
};
const TICKET_POS = REALIZADO.pos.fat / REALIZADO.pos.vendas; // R$ 2.019,04
const TICKET_OUTROS = REALIZADO.outros.fat / REALIZADO.outros.vendas; // R$ 204,42

const etapas = [
  {
    n: "0", nome: "Black 2x1 (Pós em Dobro)", periodo: "Outubro/2025",
    desc: "Lista principal + funis de entregas, grupo e venda pelo CRM do produto Pós em Dobro. Fecha antes da virada para o catálogo.",
    pontos: ["Lista principal: 24,2% de conversão, R$ 576 por lead", "Entregas: 1,5% de conversão, R$ 36 por lead", "Principal 433 vendas (R$ 1,03 mi) · entregas 24 (R$ 58 mil) · outros 240 (R$ 562 mil)", "Outubro/25 fechou R$ 1.615.732,62 (180% da meta), somado à Cert. Tornozelo"],
  },
  {
    n: "1", nome: "Virada e lançamento do catálogo", periodo: "30/10 a 09/11",
    desc: "Abre o catálogo com todas as pós, certificações e STAR, páginas customizadas com cupom e checkout Eduzz direto. CRM segue vendendo Pós em Dobro (sem venda direta).",
    pontos: ["E-mails 10 a 14 para toda a base: “Começou a Black November”", "01/11: R$ 41.678 (17 pós) · 03/11: R$ 94.985 (37 pós, maior dia da semana)", "04/11: R$ 59.944 (26 pós)"],
  },
  {
    n: "2", nome: "Sustentação e escassez", periodo: "10/11 a 23/11",
    desc: "Destaques por produto, STAR e certificações, ManyChat e escassez de lotes. Funis padrão rodando com verba baixa.",
    pontos: ["E-mails 15 a 20 + extras", "Dias mais fracos do mês (R$ 2,8 mil a R$ 43 mil/dia)", "14/11 (sexta): R$ 43.408, 23 pós"],
  },
  {
    n: "3", nome: "Black Friday + reabertura 2x1 no CRM", periodo: "24/11 a 30/11",
    desc: "Catálogo na última semana e reabertura da Pós em Dobro só para a base, com régua própria de 5 e-mails.",
    pontos: ["Régua 2x1: “Ela vai voltar…” (24/11), “Abrimos amanhã” (27/11), “Começou” e “É só até amanhã” (28/11), “Até hoje” (29/11)", "E-mails 21 a 25: última semana até último dia", "28/11: R$ 123.591,97 (61 pós, 22 STAR, 23 cert.) — maior dia"],
  },
  {
    n: "4", nome: "Cyber Week e fechamento", periodo: "01/12 a 02/12",
    desc: "Troca para o cupom CYBER25, 7 e-mails rápidos e encerramento da ação.",
    pontos: ["Time separou vendas por cupom BLACK x CYBER25 em 02/12", "Total da ação: 1.077 vendas"],
  },
];

const cupons = [
  ["2POSBLACK", "Duas pós (venda única)"],
  ["2POSBLACKASS", "Duas pós (assinatura)"],
  ["BLACKUNICA", "Pós individual à vista"],
  ["BLACKASSINATURA", "Pós individual em assinatura"],
  ["BLACKCOMERCIAL", "Uso do time comercial / CRM"],
  ["BLACKDAFEO", "Catálogo geral (STAR, cert., outros)"],
  ["BLACKDAFEO2", "Catálogo geral, segundo lote"],
  ["CYBER25", "Cyber Week (01–02/12)"],
];

const pos = [
  ["Pós em Dobro (CRM)", 209], ["Gerontologia", 72], ["Ortopedia", 64], ["Dor", 40], ["Traumatologia", 30], ["Esporte", 10],
] as const;

/** Preços praticados na Black 2025 (ticket médio realizado por ponto de preço). */
const precos = [
  { produto: "Pós em Dobro — assinatura (2POSBLACKASS)", preco: "15x no contrato", ticket: 2095.4, vendas: "dentro das 327 assinaturas", obs: "Vendida só pelo CRM; ticket médio do contrato de assinatura" },
  { produto: "Pós em Dobro — à vista (2POSBLACK)", preco: "valor único", ticket: 1763, vendas: "dentro das 98 únicas", obs: "Ticket médio das vendas únicas de pós" },
  { produto: "Pós individual — assinatura (BLACKASSINATURA)", preco: "assinatura", ticket: 2095.4, vendas: "327 assinaturas no total (Dobro + individuais)", obs: "Contrato médio R$ 2.095,40" },
  { produto: "Pós individual — à vista (BLACKUNICA)", preco: "valor único", ticket: 1763, vendas: "98 únicas no total", obs: "Ticket médio R$ 1.763" },
  { produto: "STAR Cursos (BLACKDAFEO / BLACKDAFEO2)", preco: "venda direta", ticket: 41788 / 268, vendas: "268 vendas", obs: "Ticket médio R$ 155,93" },
  { produto: "Certificações (BLACKDAFEO / BLACKDAFEO2)", preco: "venda direta", ticket: 49424 / 249, vendas: "249 vendas", obs: "Ticket médio R$ 198,49" },
];

const gastos = [
  { g: "Catálogo Black (Meta, direto)", v: 60858.73, tipo: "Black" },
  { g: "ManyChat (BM1)", v: 16060.99, tipo: "Black" },
  { g: "Meta BM2 (STAR, cert., outros)", v: 101633.19, tipo: "Black" },
  { g: "Meta BM1 — demais campanhas de pós", v: 208871.2 - 60858.73 - 3497.69 - 1237.08, tipo: "Padrão" },
  { g: "Aulas experimentais (370 leads, CPL R$ 9,45)", v: 3497.69, tipo: "Padrão" },
  { g: "Ementas (122 leads, CPL R$ 10,14)", v: 1237.08, tipo: "Padrão" },
  { g: "Google Ads (BM1)", v: 2961.38, tipo: "Padrão" },
];

const picos = [
  ["01/11", 41678, 17], ["03/11", 94985, 37], ["04/11", 59944.56, 26], ["14/11", 43408.14, 23], ["24/11", 21984.47, 14], ["28/11", 123591.97, 61], ["29/11", 33746.35, 16],
] as const;

function Card({ l, v, s }: { l: string; v: string; s?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{l}</div>
      <div className="mt-1 text-2xl font-bold text-foreground">{v}</div>
      {s && <div className="mt-1 text-xs text-muted-foreground">{s}</div>}
    </div>
  );
}

function Sec({ t, children }: { t: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="mb-4 text-xl font-bold text-foreground">{t}</h2>
      {children}
    </section>
  );
}

type LinhaMeta = { vendas: number; ticket: number };

function LinhaMetaEditor({
  titulo, sub, linha, onChange,
}: { titulo: string; sub: string; linha: LinhaMeta; onChange: (l: LinhaMeta) => void }) {
  const fat = linha.vendas * linha.ticket;
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-bold">{titulo}</h3>
        <span className="text-xs text-muted-foreground">{sub}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <label className="text-xs text-muted-foreground">
          Nº de vendas
          <input
            type="number" min={0} step={1} value={linha.vendas}
            onChange={(e) => onChange({ ...linha, vendas: Math.max(0, Number(e.target.value) || 0) })}
            className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-base text-foreground"
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Ticket médio (R$)
          <input
            type="number" min={0} step={10} value={linha.ticket}
            onChange={(e) => onChange({ ...linha, ticket: Math.max(0, Number(e.target.value) || 0) })}
            className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-base text-foreground"
          />
        </label>
        <div className="text-xs text-muted-foreground">
          Faturamento
          <div className="mt-1 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-base font-bold text-primary">
            {brl(fat)}
          </div>
        </div>
      </div>
    </div>
  );
}

function Page() {
  const [posGeral, setPosGeral] = useState<LinhaMeta>({ vendas: REALIZADO.posCatalogo.vendas, ticket: Math.round(TICKET_POS) });
  const [dobro, setDobro] = useState<LinhaMeta>({ vendas: REALIZADO.posDobro.vendas, ticket: Math.round(TICKET_POS) });
  const [outros, setOutros] = useState<LinhaMeta>({ vendas: REALIZADO.outros.vendas, ticket: Math.round(TICKET_OUTROS) });

  const fatPos = posGeral.vendas * posGeral.ticket;
  const fatDobro = dobro.vendas * dobro.ticket;
  const fatOutros = outros.vendas * outros.ticket;
  const meta = fatPos + fatDobro + fatOutros;
  const vendas = posGeral.vendas + dobro.vendas + outros.vendas;
  const invest = vendas * ACAO.cpv;
  const sess = Math.round(vendas / (ACAO.conv / 100));
  const maxPos = 209;
  const maxPico = 123591.97;

  return (
    <main className="dark min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">Inteligência interna · FEO</p>
        <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">Black November 2025</h1>
        <p className="mt-2 max-w-3xl text-muted-foreground">
          Da Black 2x1 (etapa 0) ao fechamento da Cyber Week: tudo que aconteceu de 30/10 a 02/12/2025 para basear as metas da ação deste ano.
        </p>

        <Sec t="Resultado da ação (30/10 a 02/12)">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card l="Faturamento" v={brl(ACAO.fat)} s="Líquido Eduzz" />
            <Card l="Investimento direto" v={brl(ACAO.invest, 2)} />
            <Card l="ROAS" v={num(ACAO.roas, 2)} />
            <Card l="Vendas" v={num(ACAO.vendas)} />
            <Card l="Ticket médio" v={brl(ACAO.ticket, 2)} />
            <Card l="Custo por venda" v={brl(ACAO.cpv, 2)} />
            <Card l="Sessões no catálogo" v={num(ACAO.sessoes)} />
            <Card l="Conversão" v={`${num(ACAO.conv, 2)}%`} s="Vendas ÷ sessões" />
          </div>
        </Sec>

        <Sec t="Mês de novembro completo (Placar do Jogo)">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card l="Faturamento" v={brl(MES.fat, 2)} s={`${num((MES.fat / MES.meta) * 100, 1)}% da meta de R$ 900 mil`} />
            <Card l="Gasto total (BM1+BM2)" v={brl(MES.gasto, 2)} />
            <Card l="Saldo" v={brl(MES.saldo, 2)} s={`ROAS ${num(MES.roas, 2)}`} />
            <Card l="Pós pagas" v={num(MES.pagas)} s={`+${MES.abertas} faturas abertas`} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            O mês e a ação têm escopos diferentes: a ação conta só o investimento do catálogo; o placar soma toda a mídia do mês.
          </p>
        </Sec>

        <Sec t="Meta x Realizado por linha de produto (novembro)">
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-3">Linha</th>
                  <th className="p-3 text-right">Vendas</th>
                  <th className="p-3 text-right">Faturamento</th>
                  <th className="p-3 text-right">Ticket médio</th>
                  <th className="p-3 text-right">% do faturamento</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-semibold">Pós-graduações (total)</td>
                  <td className="p-3 text-right">{num(REALIZADO.pos.vendas)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.pos.fat, 2)}</td>
                  <td className="p-3 text-right">{brl(TICKET_POS, 2)}</td>
                  <td className="p-3 text-right">{num((REALIZADO.pos.fat / (REALIZADO.pos.fat + REALIZADO.outros.fat)) * 100, 1)}%</td>
                </tr>
                <tr className="border-b border-border text-muted-foreground">
                  <td className="p-3 pl-6">· Black em Dobro pelo CRM</td>
                  <td className="p-3 text-right">{num(REALIZADO.posDobro.vendas)}</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">49% das pós</td>
                </tr>
                <tr className="border-b border-border text-muted-foreground">
                  <td className="p-3 pl-6">· Pós do catálogo (6 pós individuais)</td>
                  <td className="p-3 text-right">{num(REALIZADO.posCatalogo.vendas)}</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">51% das pós</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-semibold">Outros produtos (total)</td>
                  <td className="p-3 text-right">{num(REALIZADO.outros.vendas)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.outros.fat, 2)}</td>
                  <td className="p-3 text-right">{brl(TICKET_OUTROS, 2)}</td>
                  <td className="p-3 text-right">{num((REALIZADO.outros.fat / (REALIZADO.pos.fat + REALIZADO.outros.fat)) * 100, 1)}%</td>
                </tr>
                <tr className="border-b border-border text-muted-foreground">
                  <td className="p-3 pl-6">· STAR Cursos</td>
                  <td className="p-3 text-right">{num(REALIZADO.star.vendas)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.star.fat)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.star.fat / REALIZADO.star.vendas, 2)}</td>
                  <td className="p-3 text-right"></td>
                </tr>
                <tr className="border-b border-border text-muted-foreground">
                  <td className="p-3 pl-6">· Certificações</td>
                  <td className="p-3 text-right">{num(REALIZADO.cert.vendas)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.cert.fat)}</td>
                  <td className="p-3 text-right">{brl(REALIZADO.cert.fat / REALIZADO.cert.vendas, 2)}</td>
                  <td className="p-3 text-right"></td>
                </tr>
                <tr className="text-muted-foreground">
                  <td className="p-3 pl-6">· Extensões e outros</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">{brl(REALIZADO.extensoes.fat)}</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right"></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            A divisão de faturamento entre Pós em Dobro e pós do catálogo não foi registrada separadamente em novembro; o quadro de pagamento abaixo mostra a divisão por forma de pagamento.
          </p>
        </Sec>

        <Sec t="Etapas da ação">
          <div className="space-y-4">
            {etapas.map((e) => (
              <div key={e.n} className="rounded-xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="rounded-full bg-primary px-3 py-1 text-sm font-bold text-primary-foreground">Etapa {e.n}</span>
                  <h3 className="text-lg font-bold">{e.nome}</h3>
                  <span className="text-sm text-muted-foreground">{e.periodo}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{e.desc}</p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                  {e.pontos.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Sec>

        <Sec t="Vendas por produto (novembro)">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-bold">Pós-graduações · 425 vendas · {brl(858066.5, 2)}</h3>
              <div className="mt-4 space-y-2">
                {pos.map(([n, v]) => (
                  <div key={n}>
                    <div className="flex justify-between text-sm"><span>{n}</span><span className="font-semibold">{v}</span></div>
                    <div className="h-2 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${(v / maxPos) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">49% das pós do mês vieram da Pós em Dobro pelo CRM.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-bold">Forma de pagamento das pós</h3>
              <table className="mt-3 w-full text-sm">
                <tbody>
                  <tr className="border-b border-border"><td className="py-2">Assinatura</td><td>327 (76,9%)</td><td className="text-right">{brl(685297.5, 2)} contrato</td></tr>
                  <tr className="border-b border-border"><td className="py-2">1ª parcela assinatura</td><td></td><td className="text-right">{brl(45844.3, 2)}</td></tr>
                  <tr><td className="py-2">Única (à vista)</td><td>98 (23,1%)</td><td className="text-right">{brl(172769)}</td></tr>
                </tbody>
              </table>
              <p className="mt-2 text-xs text-muted-foreground">Ticket médio: única {brl(172769 / 98, 2)} · assinatura {brl(685297.5 / 327, 2)} · geral {brl(2028.51, 2)}</p>
              <h3 className="mt-5 font-bold">Outros produtos · {brl(105684)}</h3>
              <table className="mt-2 w-full text-sm">
                <tbody>
                  <tr className="border-b border-border"><td className="py-2">STAR Cursos</td><td>268</td><td className="text-right">{brl(41788)}</td></tr>
                  <tr className="border-b border-border"><td className="py-2">Certificações</td><td>249</td><td className="text-right">{brl(49424)}</td></tr>
                  <tr><td className="py-2">Extensões e outros</td><td>—</td><td className="text-right">{brl(15654)}</td></tr>
                </tbody>
              </table>
              <p className="mt-2 text-xs text-muted-foreground">Ticket: STAR {brl(41788 / 268, 2)} · cert. {brl(49424 / 249, 2)}</p>
            </div>
          </div>
        </Sec>

        <Sec t="Preços praticados por produto">
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-3">Produto (cupom)</th>
                  <th className="p-3">Formato</th>
                  <th className="p-3 text-right">Ticket médio realizado</th>
                  <th className="p-3">Base</th>
                </tr>
              </thead>
              <tbody>
                {precos.map((p) => (
                  <tr key={p.produto} className="border-b border-border">
                    <td className="p-3 font-semibold">{p.produto}</td>
                    <td className="p-3 text-muted-foreground">{p.preco}</td>
                    <td className="p-3 text-right font-semibold text-primary">{brl(p.ticket, 2)}</td>
                    <td className="p-3 text-xs text-muted-foreground">{p.vendas} — {p.obs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            São tickets médios realizados (receita ÷ vendas), não o preço de vitrine: cupons, juros de parcelamento e lotes misturam preços diferentes dentro de cada linha. As 6 pós do catálogo foram vendidas pelos mesmos dois pontos de preço (à vista ou assinatura).
          </p>
        </Sec>

        <Sec t="Cupons usados na Eduzz">
          <div className="grid gap-2 md:grid-cols-2">
            {cupons.map(([c, d]) => (
              <div key={c} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
                <code className="font-bold text-primary">{c}</code>
                <span className="text-sm text-muted-foreground">{d}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">A finalidade de cada cupom é inferida pelo nome; confirmar no painel Eduzz.</p>
        </Sec>

        <Sec t="Gastos: campanhas Black x padrão (novembro)">
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Campanha</th><th className="p-3">Tipo</th><th className="p-3 text-right">Gasto</th></tr></thead>
              <tbody>
                {gastos.map((g) => (
                  <tr key={g.g} className="border-b border-border">
                    <td className="p-3">{g.g}</td>
                    <td className="p-3"><span className={g.tipo === "Black" ? "font-semibold text-primary" : "text-muted-foreground"}>{g.tipo}</span></td>
                    <td className="p-3 text-right">{brl(g.v, 2)}</td>
                  </tr>
                ))}
                <tr className="font-bold"><td className="p-3">Total do mês</td><td></td><td className="p-3 text-right">{brl(MES.gasto, 2)}</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">“Demais campanhas de pós” é a diferença entre o Meta BM1 total e o que foi identificado; inclui Pós em Dobro, remarketing e perpétuos.</p>
        </Sec>

        <Sec t="Melhores dias">
          <div className="space-y-2">
            {picos.map(([d, v, p]) => (
              <div key={d} className="flex items-center gap-3 text-sm">
                <span className="w-12 font-semibold">{d}</span>
                <div className="h-6 flex-1 rounded bg-muted"><div className="h-6 rounded bg-primary" style={{ width: `${(v / maxPico) * 100}%` }} /></div>
                <span className="w-40 text-right">{brl(v)} · {p} pós</span>
              </div>
            ))}
          </div>
        </Sec>

        <Sec t="Aprendizados para 2026">
          <ul className="list-disc space-y-2 pl-5 text-sm">
            <li>A Pós em Dobro no CRM respondeu por quase metade das pós de novembro: manter a reabertura na semana da Black Friday.</li>
            <li>Segundas após disparo e a sexta da Black Friday concentram o faturamento; reforçar verba e equipe nesses dias.</li>
            <li>O meio do mês (10 a 23/11) foi o vale: planejar gatilho novo (bônus, lote, produto em destaque).</li>
            <li>BM2 teve ROAS próximo de 1 (R$ 105 mil de venda para R$ 101 mil de gasto): revisar verba de STAR/cert.</li>
            <li>Lista principal converte 16x mais que entregas: priorizar captação da lista principal na etapa 0.</li>
            <li>Ação sem fechamento registrado no Discord: usar o checklist de encerramento das ações.</li>
          </ul>
        </Sec>

        <Sec t="Balizador de metas 2026 (mesma eficiência de 2025)">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">
              Monte a meta por linha de produto: edite o número de vendas e o ticket médio de cada uma. O faturamento de cada linha e a meta total são calculados na hora. Os campos vêm pré-preenchidos com o realizado de 2025.
            </p>
            <div className="mt-4 space-y-3">
              <LinhaMetaEditor
                titulo="Pós geral (catálogo)"
                sub={`Realizado 2025: ${REALIZADO.posCatalogo.vendas} vendas · ticket médio ${brl(TICKET_POS, 2)}`}
                linha={posGeral}
                onChange={setPosGeral}
              />
              <LinhaMetaEditor
                titulo="Black em Dobro pelo CRM"
                sub={`Realizado 2025: ${REALIZADO.posDobro.vendas} vendas pelo CRM`}
                linha={dobro}
                onChange={setDobro}
              />
              <LinhaMetaEditor
                titulo="Outros produtos (STAR, certificações, extensões)"
                sub={`Realizado 2025: ${REALIZADO.outros.vendas} vendas · ticket médio ${brl(TICKET_OUTROS, 2)}`}
                linha={outros}
                onChange={setOutros}
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Card l="Meta de faturamento" v={brl(meta)} s={`Pós ${brl(fatPos + fatDobro)} · outros ${brl(fatOutros)}`} />
              <Card l="Vendas necessárias" v={num(vendas)} s={`${posGeral.vendas + dobro.vendas} pós · ${outros.vendas} outros`} />
              <Card l="Investimento estimado" v={brl(invest)} s={`CPV ${brl(ACAO.cpv, 2)}`} />
              <Card l="Sessões no catálogo" v={num(sess)} s={`Conversão ${num(ACAO.conv, 2)}%`} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Card l="Crescimento x 2025" v={`${num(((meta / ACAO.fat) - 1) * 100, 1)}%`} s={`Ação 2025: ${brl(ACAO.fat)}`} />
              <Card l="ROAS projetado" v={num(invest > 0 ? meta / invest : 0, 2)} s={`ROAS 2025: ${num(ACAO.roas, 2)}`} />
              <Card l="Ticket médio projetado" v={brl(vendas > 0 ? meta / vendas : 0, 2)} s={`2025: ${brl(ACAO.ticket, 2)}`} />
              <Card l="Participação das pós" v={`${num(meta > 0 ? ((fatPos + fatDobro) / meta) * 100 : 0, 1)}%`} s="Do faturamento da meta" />
            </div>
          </div>
        </Sec>

        <p className="mt-10 text-xs text-muted-foreground">
          Fontes: planilha mestre de ações (aba Catálogo), Placar do Jogo e Leads de nov/25, Metas x Realizado 2025, calendário de disparos 2025 e comparativo das listas da Black 2x1 2025.
        </p>
      </div>
    </main>
  );
}
